import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

// Palette de gradients et d'icônes par culture
const FALLBACK_CROP_THEMES = {
  "manioc": {
    gradient: "linear-gradient(135deg, #1b4332 0%, #2d6a4f 60%, #40916c 100%)",
    icon: "🌱",
    subtitle: "Tubercule & Féculent • Origine Gabon"
  },
  "plantain": {
    gradient: "linear-gradient(135deg, #78350f 0%, #b45309 50%, #f59e0b 100%)",
    icon: "🍌",
    subtitle: "Banane Plantain • Bassin Gabonais"
  },
  "banane": {
    gradient: "linear-gradient(135deg, #78350f 0%, #b45309 50%, #f59e0b 100%)",
    icon: "🍌",
    subtitle: "Banane Plantain • Bassin Gabonais"
  },
  "tomate": {
    gradient: "linear-gradient(135deg, #881337 0%, #be123c 60%, #e11d48 100%)",
    icon: "🍅",
    subtitle: "Maraîchage Frais • Plein Champ & Serre"
  },
  "ananas": {
    gradient: "linear-gradient(135deg, #854d0e 0%, #ca8a04 50%, #eab308 100%)",
    icon: "🍍",
    subtitle: "Ananas Victoria & Pain de Sucre"
  },
  "piment": {
    gradient: "linear-gradient(135deg, #7f1d1d 0%, #b91c1c 60%, #ef4444 100%)",
    icon: "🌶️",
    subtitle: "Piment Équatorial • Cueillette Fraîche"
  },
  "aubergine": {
    gradient: "linear-gradient(135deg, #3b0764 0%, #6b21a8 60%, #9333ea 100%)",
    icon: "🍆",
    subtitle: "Maraîchage Traditionnel • Qualité Supérieure"
  },
  "cacao": {
    gradient: "linear-gradient(135deg, #271306 0%, #451a03 50%, #78350f 100%)",
    icon: "🍫",
    subtitle: "Cacao Grand Cru • Fèves Fermentées"
  },
  "café": {
    gradient: "linear-gradient(135deg, #180d05 0%, #361705 60%, #5c2409 100%)",
    icon: "☕",
    subtitle: "Café Robusta • Récolte Forestière"
  },
  "passion": {
    gradient: "linear-gradient(135deg, #78350f 0%, #b45309 50%, #eab308 100%)",
    icon: "✨",
    subtitle: "Maracuja / Fruit de la Passion"
  },
  "default": {
    gradient: "linear-gradient(135deg, #01321d 0%, #014527 60%, #035417 100%)",
    icon: "🌾",
    subtitle: "Produit Certifié AVG Farm"
  }
};

const getThemeForProduct = (productName = "") => {
  const lower = productName.toLowerCase();
  for (const [key, theme] of Object.entries(FALLBACK_CROP_THEMES)) {
    if (key !== "default" && lower.includes(key)) {
      return theme;
    }
  }
  return FALLBACK_CROP_THEMES.default;
};

/**
 * Composant ProductImage 100% stable et robuste :
 * - Rendu immédiat sans masquage ni scintillement
 * - Ratio strict (155px)
 * - Dégradé protecteur pour assurer un contraste parfait des badges et du prix
 * - Remplacement automatique en cas de rupture
 */
const ProductImage = ({ src, alt, height = 165 }) => {
  const [hasError, setHasError] = useState(false);
  const theme = getThemeForProduct(alt);
  const resolvedSrc = typeof src === "object" && src?.default ? src.default : src;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height,
        bgcolor: "#0f2316",
        overflow: "hidden"
      }}
    >
      {hasError || !resolvedSrc ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            background: theme.gradient,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            p: 2,
            textAlign: "center"
          }}
        >
          <Typography variant="h4" sx={{ mb: 0.5, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))" }}>
            {theme.icon}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: "0.4px", lineHeight: 1.2 }}>
            {alt}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.68rem", mt: 0.5 }}>
            {theme.subtitle}
          </Typography>
        </Box>
      ) : (
        <Box
          component="img"
          src={resolvedSrc}
          alt={alt}
          onError={() => setHasError(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            transition: "transform 0.4s ease",
            "&:hover": {
              transform: "scale(1.06)"
            }
          }}
        />
      )}

      {/* Dégradé supérieur pour contraster les badges et titres (toujours actif au-dessus de l'image) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.85) 100%)",
          pointerEvents: "none"
        }}
      />
    </Box>
  );
};

export default ProductImage;
